import { useMemo } from 'react'
import type { MatchDetailResponse } from '../../../../../../api/useSupplierReconciliation/useMatchDetail'
import type { components } from '../../../../../../types/api/v1'

type BolCandidate = components['schemas']['BolCandidate']
type InvoiceCandidate = components['schemas']['InvoiceCandidate']

interface MatchDocumentsState {
  invoices: InvoiceCandidate[]
  bols: BolCandidate[]
}

export function useMatchDocumentProcessing(matchDetails: MatchDetailResponse) {
  // Initialize ALL match items from existing match (both invoices and BOLs)
  const initialMatchDocuments = useMemo(() => {
    if (!matchDetails?.document_detail_rows) return { invoices: [], bols: [] }

    // Group documents by ID and type to handle multiple product rows per document
    const invoiceMap = new Map()
    const bolMap = new Map()

    matchDetails.document_detail_rows.forEach((row) => {
      const isInvoice = row.document_type === 'invoice'
      const documentMap = isInvoice ? invoiceMap : bolMap

      const docId = row.document_id
      if (!documentMap.has(docId)) {
        // First time seeing this document - create the base structure
        if (!isInvoice) {
          const bolTerminalName = row.terminal?.internal_name || row.terminal?.source_name || row.terminal_name

          documentMap.set(docId, {
            original: true, // Mark as original match item
            id: `bol_${docId}`,
            bol_id: docId,
            bol_number: row.document_number,
            bol_date: row.document_date,
            order_number: row.order_number,
            supplier_name: matchDetails.supplier_name,
            terminal_name: bolTerminalName,
            destination_name: row.destination_name,
            store_number: row.store_number,
            products: [],
            bol_total: matchDetails.bol_total || row.total || 0,
            // Add volume fields at document level
            gross_volume: 0,
            net_volume: 0,
          })
        } else {
          const invoiceTerminalName = row.terminal?.internal_name || row.terminal?.source_name || row.terminal_name

          documentMap.set(docId, {
            original: true, // Mark as original match item
            id: `invoice_${docId}`,
            invoice_id: docId,
            invoice_number: row.document_number,
            invoice_date: matchDetails.invoice_date, // Use parent match-level invoice_date
            supplier_name: matchDetails.supplier_name,
            terminal_name: invoiceTerminalName,
            details: [],
            detailsMap: new Map(), // Temporary map to group by product
            invoice_total: matchDetails.invoice_total || row.total || 0,
            // Add volume fields from row data (will be updated if multiple rows exist)
            net_volume: row.net_volume || 0,
            gross_volume: row.gross_volume || 0,
            billed_volume: row.billed_volume || 0,
          })
        }
      }

      // Add product information to the document
      const doc = documentMap.get(docId)

      // For BOLs - each row represents a product line
      if (!isInvoice) {
        // Use product.internal_name from the product object
        const productName = row.product?.internal_name || row.product?.source_name || 'Unknown Product'
        doc.products.push({
          product_name: productName,
          gross_volume: row.gross_volume || 0,
          net_volume: row.net_volume || 0,
          product_total: row.product_total || 0,
          tax_total: row.tax_total || 0,
        })

        // Accumulate volumes at document level
        doc.gross_volume += row.gross_volume || 0
        doc.net_volume += row.net_volume || 0
      } else {
        // Group by product to avoid duplicates
        const productKey = row.product?.internal_name || 'Unknown Product'
        const existingDetail = doc.detailsMap.get(productKey)

        if (existingDetail) {
          // Add to existing product detail
          existingDetail.total += (row.product_total || 0) + (row.tax_total || 0)
          existingDetail.product_total = (existingDetail.product_total || 0) + (row.product_total || 0)
          existingDetail.tax_total = (existingDetail.tax_total || 0) + (row.tax_total || 0)
          existingDetail.gross_volume += row.gross_volume || 0
        } else {
          // Create new product detail with type='supply'
          doc.detailsMap.set(productKey, {
            type: 'supply',
            product: {
              internal_name: row.product?.internal_name || 'Unknown Product',
              source_name: row.product?.source_name || row.product?.internal_name || 'Unknown Product',
            },
            total: (row.product_total || 0) + (row.tax_total || 0),
            product_total: row.product_total || 0,
            tax_total: row.tax_total || 0,
            bol_number: row.order_number || null,
            gross_volume: row.gross_volume || 0,
          })
        }
      }
    })

    // Create BOL number to date mapping for invoice BOL date resolution
    const bolNumberToDateMap = new Map<string, string>()
    matchDetails.document_detail_rows?.forEach((row) => {
      if (row.document_type === 'bol' && row.bol_number && row.document_date) {
        bolNumberToDateMap.set(row.bol_number, row.document_date)
      }
    })

    // Convert maps to arrays and process
    const processDocuments = (
      docMap: Map<string, any>,
      isInvoiceType: boolean
    ): (InvoiceCandidate | BolCandidate)[] => {
      return Array.from(docMap.values()).map((doc) => {
        if (doc.detailsMap) {
          // Convert supply details
          doc.details = Array.from(doc.detailsMap.values())

          // Add tax transactions as details with type='tax'
          if (isInvoiceType && matchDetails.invoice_tax_transactions) {
            // Filter tax transactions for this invoice
            const invoiceTaxTransactions = matchDetails.invoice_tax_transactions.filter(
              (tax) => tax.document_id === doc.invoice_id
            )

            // Add each tax transaction as a detail with type='tax'
            invoiceTaxTransactions.forEach((tax) => {
              doc.details.push({
                type: 'tax',
                product: {
                  internal_name: tax.product?.internal_name || 'Tax',
                  source_name: tax.product?.source_name || 'Tax',
                },
                total: tax.tax_total || 0,
                tax_total: tax.tax_total || 0,
                product_total: 0,
                tax_type: tax.tax_type,
                tax_authority: tax.tax_authority,
                state: tax.state,
              })
            })
          }

          delete doc.detailsMap // Remove temporary map
        }
        return doc
      })
    }

    const invoices = processDocuments(invoiceMap, true)
    const bols = processDocuments(bolMap, false)

    // Set invoice_bol_date for invoices based on their BOL number, fallback to invoice document_date
    invoices.forEach((invoice) => {
      // Find the BOL number for this invoice from the original document rows
      const invoiceRow = matchDetails.document_detail_rows?.find(
        (row) => row.document_type === 'invoice' && row.document_id === invoice.invoice_id
      )
      if (invoiceRow?.bol_number) {
        const bolDate = bolNumberToDateMap.get(invoiceRow.bol_number)
        if (bolDate) {
          ;(invoice as any).invoice_bol_date = bolDate
        } else {
          // Fallback: use invoice's own document_date if no BOL date found
          ;(invoice as any).invoice_bol_date = invoiceRow.document_date
        }
      } else {
        // Fallback: use invoice's own document_date if no bol_number
        if (invoiceRow?.document_date) {
          ;(invoice as any).invoice_bol_date = invoiceRow.document_date
        }
      }
    })

    return { invoices, bols }
  }, [matchDetails])

  return {
    initialMatchDocuments,
  }
}
